export type MessageType =
	| "getAuth0User"
	| "getAccessTokenSilently"
	| "getIdTokenClaims"
	| "iframeReady"
	| "isAuthenticated"
	| "loginWithRedirect"
	| "logoutWithRedirect"
	| "ping"
	| "routerPathChange"
	| "setTheme";

export interface MessageDataArgs {
	type: MessageType;
	args?: unknown;
}

/**
 * Returns `true` if the current window's parent is not
 * itself. In other words, the current window is running
 * inside an iframe if this function returns `true`.
 *
 * **NOTE**: This function must be called inside hooks
 * to ensure it runs only on the client-side.
 */
export const isRunningInIframe = () => {
	if (typeof window === "undefined") {
		return false;
	}

	return window.parent !== window.self;
};

export const sendPostMessage = (messageType: MessageType, args?: unknown, origin?: string) => {
	if (!isRunningInIframe()) {
		throw new Error("This function cannot be called in the parent frame");
	}

	// I am leaving this in as it was pretty helpful to debug issues with iframe message passing.
	//
	// console.log("sendPostMessage called with the following", {
	// 	parentUrl,
	// 	messageType,
	// 	args,
	// 	origin,
	// });

	// The "*" for target origin is safe here since we use a CSP
	// to enfore frame-ancestors.
	window.parent.postMessage({ type: messageType, args }, origin ?? "*");
};

export async function invokePostMessage<T>(
	messageType: MessageType,
	args?: unknown,
	origin?: string
): Promise<T | undefined> {
	return await new Promise<T | undefined>((resolve, reject) => {
		if (!isRunningInIframe()) {
			reject(new Error("This function cannot be called in the parent frame"));
			return;
		}

		const channel = new MessageChannel();
		channel.port1.start();
		channel.port1.addEventListener("message", (event: MessageEvent) => {
			channel.port1.close();

			if (event.data?.err) {
				reject(event.data.err);
				return;
			}
			if (event.data) {
				resolve(event.data as T);
				return;
			}

			resolve(undefined);
		});
		// The "*" for target origin is safe here since we use a CSP
		// to enfore frame-ancestors.
		window.parent.postMessage({ type: messageType, args }, origin ?? "*", [channel.port2]);
	});
}
